import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BaseDialogComponent } from '@src/app/modules/base';
import { ChatTypeEnum } from '@src/api-messenger';
import { AlertService } from '@src/core/services';

import { ApiService, WebSocketService } from '../../services';
import { mapUserUIToAddMemberModel } from '../../utils';
import { ChatMultimediaType } from '../types';
import { AvatarService } from '../../components/photo/services';

import { ApiDataType, ContentDataType, FormControlsType, FormDataType } from './types';

@Component({
  selector: 'app-chat-create-form',
  templateUrl: './chat-create-form.component.html',
  styleUrls: ['./chat-create-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatCreateFormComponent extends BaseDialogComponent<ContentDataType, string | undefined> {
  private readonly api = inject(ApiService);
  private readonly wss = inject(WebSocketService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly avatarService = inject(AvatarService);

  loading = false;
  newPhoto?: ChatMultimediaType;

  form = new FormGroup<FormControlsType>({
    chatName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    chatDescription: new FormControl(''),
    onlyForAdmins: new FormControl(false, { nonNullable: true }),
    members: new FormControl([], { nonNullable: true }),
    attachObject: new FormControl(this.data?.attachObject ?? null),
  });

  get authUserId() {
    return this.wss.authUserId;
  }

  onPhotoChange(newPhoto?: ChatMultimediaType): void {
    this.newPhoto = newPhoto;
    this.cdr.markForCheck();
  }

  override cancel() {
    return super.cancel(undefined);
  }

  override apply() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.error(this.translateService.instant('common.alerts.errors.fillAndCheckFields'));
      return;
    }

    this.loading = true;

    const formData = this.form.getRawValue();

    const apiData: ApiDataType = this.mapFormDataToApiData(formData);
    this.api.createChat(apiData).subscribe(res => {
      const { id } = res;
      const { members } = formData;

      if (this.newPhoto) {
        this.avatarService.editChatAvatar(id, this.newPhoto.file).subscribe();
      }

      if (!members.length) {
        return super.apply(id);
      } else {
        const addMembers = mapUserUIToAddMemberModel(members);
        if (id && addMembers.length) {
          this.api.addChatMembers(id, addMembers).then(() => {
            return super.apply(id);
          });
        }
      }
    });
  }

  private mapFormDataToApiData(data: FormDataType) {
    const { chatName, chatDescription, onlyForAdmins, attachObject } = data;
    const apiData: ApiDataType = {
      chatName,
      chatDescription,
      onlyForAdmins,
      chatType: ChatTypeEnum.Chat,
      attachId: attachObject?.id,
      attachObjectName: attachObject?.name,
    };
    return apiData;
  }
}
